import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    Grid,
    Avatar,
    Rating,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowLeft,
    AlertTriangle
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReviewById } from '../../Services/ReviewManagement/ReviewApis';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';

const ReviewDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Data states
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Format date helper
    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return String(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            ...(includeTime ? { hour: '2-digit', minute: '2-digit' } : {})
        });
    };

    // Fetch review details by ID
    const fetchReviewDetail = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getReviewById(id);
            if (response && response.success && response.data) {
                setReview(response.data);
            } else {
                setError(response?.message || "Failed to load review details.");
            }
        } catch (err) {
            console.error("Error fetching review detail:", err);
            setError("Failed to fetch review details. Please check the review ID or network connection.");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchReviewDetail();
        }
    }, [id, fetchReviewDetail]);

    // Render loading state
    if (loading) {
        return (
            <PermissionWrapper permission="view">
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={32} sx={{ color: '#FFBD65' }} />
                </Box>
            </PermissionWrapper>
        );
    }

    // Render error state if review not found
    if (error || !review) {
        return (
            <PermissionWrapper permission="view">
                <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                    <Button
                        size="small"
                        startIcon={<ArrowLeft size={16} />}
                        onClick={() => navigate('/review-management/reviews')}
                        sx={{
                            color: '#475569',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            mb: 2.5,
                            '&:hover': { bgcolor: '#F1F5F9' }
                        }}
                    >
                        Back to Reviews
                    </Button>
                    <Alert severity="error" sx={{ borderRadius: '6px' }}>
                        {error || "Review details could not be found."}
                    </Alert>
                </Box>
            </PermissionWrapper>
        );
    }

    const guestName = review.showAsName?.trim() || review.cuName?.trim() || 'Anonymous guest';
    const initialChar = guestName.charAt(0).toUpperCase() || 'G';
    const isPublish = review.status === 'publish';
    const isReject = review.status === 'reject';
    const isPending = review.status === 'pending';
    const isExternal = Boolean(review.isexternalLinkByHost);
    const overallRatingNum = parseFloat(review.overallRating) || 0;

    // Category ratings list (8 categories)
    const categoryRatings = [
        { label: 'Program Quality', value: review.programQuality },
        { label: 'Location', value: review.location },
        { label: 'Staff Behavior / Teachers', value: review.staffBehavior },
        { label: 'Food Quality', value: review.foodQuality },
        { label: 'Accommodation', value: review.accommodation },
        { label: 'Description Accuracy', value: review.descriptionAccuracy },
        { label: 'Value for Money', value: review.valueForMoney },
        { label: 'Communication', value: review.communication }
    ].filter(c => c.value !== undefined && c.value !== null && c.value !== '');

    return (
        <PermissionWrapper permission="view">
            <Box
                sx={{
                    p: { xs: 2, md: 4 },
                    bgcolor: '#FBFBFB',
                    minHeight: '100vh',
                    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif"
                }}
            >
                {/* 1. TOP HEADER & NAVIGATION */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArrowLeft size={15} />}
                            onClick={() => navigate('/review-management/reviews')}
                            sx={{
                                borderColor: '#E2E8F0',
                                color: '#475569',
                                fontSize: '0.78rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                px: 1.75,
                                py: 0.5,
                                borderRadius: '6px',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#FFFFFF' }
                            }}
                        >
                            Back to Reviews
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '1.15rem', fontWeight: 600, color: '#1E293B' }}>
                                Review #{review.id}
                            </Typography>
                            <Chip
                                size="small"
                                label={isReject ? 'rejected' : (isPublish ? 'published' : (isPending ? 'pending' : (review.status || 'pending')))}
                                sx={{
                                    height: 22,
                                    fontSize: '0.68rem',
                                    fontWeight: 500,
                                    borderRadius: '12px',
                                    px: 0.75,
                                    bgcolor: isReject ? '#FEF2F2' : (isPublish ? '#F0FDF4' : '#FFFBEB'),
                                    color: isReject ? '#DC2626' : (isPublish ? '#166534' : '#D97706'),
                                    border: `1px solid ${isReject ? '#FECACA' : (isPublish ? '#BBF7D0' : '#FDE68A')}`
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* 2. REJECTION NOTICE IF STATUS IS REJECT */}
                {review.rejectReason && (
                    <Alert
                        severity="error"
                        icon={<AlertTriangle size={18} />}
                        sx={{
                            mb: 3,
                            borderRadius: '6px',
                            border: '1px solid #FECACA',
                            bgcolor: '#FEF2F2',
                            color: '#991B1B',
                            fontSize: '0.78rem'
                        }}
                    >
                        <strong>Rejection Reason:</strong> {review.rejectReason}
                    </Alert>
                )}

                {/* 3. MAIN CONTENT GRID (2 COLUMNS) */}
                <Grid container spacing={3}>
                    {/* LEFT COLUMN: PRIMARY REVIEW DETAILS, QUESTIONNAIRE, PHOTOS, REPLIES */}
                    <Grid item size={{ xs: 12, lg: 8 }}>
                        <Stack spacing={3}>
                            {/* Card 1: Reviewer & Retreat Overview */}
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    p: 3,
                                    boxShadow: 'none'
                                }}
                            >
                                {/* Reviewer Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            src={review.reviewerImage || undefined}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: '#FAF5EE',
                                                color: '#3D1F10',
                                                fontSize: '1rem',
                                                fontWeight: 600,
                                                border: '1px solid #E2E8F0'
                                            }}
                                        >
                                            {initialChar}
                                        </Avatar>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                                                    {guestName}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={isExternal ? 'external — host link' : 'verified booking'}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.68rem',
                                                        fontWeight: 500,
                                                        borderRadius: '12px',
                                                        px: 0.5,
                                                        bgcolor: isExternal ? '#EFF6FF' : '#F0FDF4',
                                                        color: isExternal ? '#0284C7' : '#166534',
                                                        border: `1px solid ${isExternal ? '#BAE6FD' : '#BBF7D0'}`
                                                    }}
                                                />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.76rem', color: '#64748B', mt: 0.25 }}>
                                                Submitted on {formatDate(review.createdAt, true)}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Score Pill */}
                                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', px: 2, py: 1 }}>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Overall Rating
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                                            <Rating value={overallRatingNum} precision={0.1} readOnly size="small" sx={{ color: '#FFBD65' }} />
                                            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                                                {overallRatingNum.toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Retreat & Center Information Box */}
                                <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2, mb: 2.5 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Retreat Program
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B', mt: 0.25 }}>
                                                {review.retreatName || 'Retreat'}
                                            </Typography>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Wellness Center / Host
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B', mt: 0.25 }}>
                                                {review.centerName || 'Wellness Center'}
                                            </Typography>

                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Review Title & Testimonial Quote */}
                                {review.title && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
                                            Guest Review Statement
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.88rem', color: '#334155', fontStyle: 'italic', lineHeight: 1.6, bgcolor: '#FFFFFF', p: 2, borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                                            "{review.title}"
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>

                            {/* Card 2: Rating Breakdown (All 8 Categories) */}
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    p: 3,
                                    boxShadow: 'none'
                                }}
                            >
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                    Rating Breakdown
                                </Typography>
                                <Grid container spacing={1.5}>
                                    {categoryRatings.map((rating) => {
                                        const val = parseFloat(rating.value) || 0;
                                        return (
                                            <Grid item xs={12} sm={6} md={3} key={rating.label}>
                                                <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                                                        {rating.label}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                                        <Rating value={val} readOnly size="small" precision={0.1} sx={{ color: '#FFBD65', fontSize: '0.9rem' }} />
                                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B' }}>
                                                            {val.toFixed(1)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Paper>

                            {/* Card 3: Questionnaire Answers */}
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    p: 3,
                                    boxShadow: 'none'
                                }}
                            >
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                    Questionnaire Responses & Feedback
                                </Typography>
                                {review.answers && review.answers.length > 0 ? (
                                    <Stack spacing={2}>
                                        {review.answers.map((ans, idx) => (
                                            <Box key={ans.id || idx} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1E293B', mb: 0.75 }}>
                                                    {ans.question?.question || `Question #${ans.questionId || idx + 1}`}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.6 }}>
                                                    "{ans.answer}"
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                        <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                                            No questionnaire responses submitted for this review.
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>


                        </Stack>
                    </Grid>

                    {/* RIGHT COLUMN: METADATA & SIDEBAR SUMMARY */}
                    <Grid item size={{ xs: 12, lg: 4 }}>
                        <Stack spacing={3}>
                            {/* Metadata Card */}
                            <Paper
                                elevation={0}
                                sx={{
                                    bgcolor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '8px',
                                    p: 2.5,
                                    boxShadow: 'none'
                                }}
                            >
                                <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                    Review Metadata
                                </Typography>

                                <Stack spacing={1.75}>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Verification Type</Typography>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: isExternal ? '#0284C7' : '#166534' }}>
                                            {isExternal ? 'External Host Link' : 'Verified Platform Booking'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Current Status</Typography>
                                        <Chip
                                            size="small"
                                            label={review.status}
                                            sx={{
                                                height: 20,
                                                fontSize: '0.66rem',
                                                fontWeight: 500,
                                                bgcolor: isPublish ? '#F0FDF4' : (isReject ? '#FEF2F2' : '#FFFBEB'),
                                                color: isPublish ? '#166534' : (isReject ? '#DC2626' : '#D97706'),
                                                border: `1px solid ${isPublish ? '#BBF7D0' : (isReject ? '#FECACA' : '#FDE68A')}`
                                            }}
                                        />
                                    </Box>

                                    {/* Audit Details Divider */}
                                    <Box sx={{ borderTop: '1px solid #F1F5F9', pt: 1, mt: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', mb: 1.5 }}>
                                            Audit Details
                                        </Typography>
                                    </Box>

                                    {/* Created By (cu) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Created By</Typography>
                                            <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>
                                                {review.cuType ? `Type: ${review.cuType}` : ''} {review.cuId ? `· ID: #${review.cuId}` : ''}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: '#1E293B', textAlign: 'right' }}>
                                            {review.cuName || '—'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Created At</Typography>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: '#1E293B' }}>
                                            {formatDate(review.createdAt, true)}
                                        </Typography>
                                    </Box>

                                    {/* Updated By (uu) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Updated By</Typography>
                                            <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8' }}>
                                                {review.uuType ? `Type: ${review.uuType}` : ''} {review.uuId ? `· ID: #${review.uuId}` : ''}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: '#1E293B', textAlign: 'right' }}>
                                            {review.uuName || '—'}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748B' }}>Updated At</Typography>
                                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 500, color: '#1E293B' }}>
                                            {formatDate(review.updatedAt, true)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        </Stack>
                        <Paper
                            elevation={0}
                            sx={{
                                bgcolor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '8px',
                                p: 3,
                                mt: 2,
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                Guest Photos ({review.photos ? review.photos.length : 0})
                            </Typography>
                            {review.photos && review.photos.length > 0 ? (
                                <Grid container spacing={2}>
                                    {review.photos.map((photo, idx) => (
                                        <Grid item xs={6} sm={4} md={3} key={photo.id || idx}>
                                            <Box
                                                sx={{
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    bgcolor: '#FFFFFF',
                                                    cursor: 'pointer',
                                                    position: 'relative'
                                                }}
                                                onClick={() => window.open(photo.imageUrl, '_blank')}
                                            >
                                                <Box
                                                    component="img"
                                                    src={photo.imageUrl}
                                                    alt={photo.photoType || 'Guest Photo'}
                                                    sx={{
                                                        width: '100%',
                                                        height: 140,
                                                        objectFit: 'cover',
                                                        display: 'block'
                                                    }}
                                                />
                                                {photo.photoType && (
                                                    <Chip
                                                        size="small"
                                                        label={photo.photoType.toUpperCase()}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 6,
                                                            left: 6,
                                                            height: 20,
                                                            fontSize: '0.62rem',
                                                            fontWeight: 600,
                                                            bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                            color: '#475569',
                                                            border: '1px solid #E2E8F0'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                                        No photos uploaded for this review.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                        <Paper
                            elevation={0}
                            sx={{
                                bgcolor: '#FFFFFF',
                                border: '1px solid #E2E8F0',
                                borderRadius: '8px',
                                p: 3,
                                mt: 3,
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                Response & Moderation
                            </Typography>

                            {review.replayDescription ? (
                                <Box sx={{ bgcolor: '#FFFDF9', border: '1px solid #F6EBD9', borderRadius: '6px', p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#78350F' }}>
                                            {review.replayBy === 'admin' ? 'Official Reply from Heal at India' : `Host Reply · ${review.replayByUserName || review.centerName || 'Host'}`}
                                        </Typography>
                                        {review.replayStatus && (
                                            <Chip
                                                size="small"
                                                label={review.replayStatus}
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.62rem',
                                                    fontWeight: 500,
                                                    borderRadius: '10px',
                                                    bgcolor: '#FFFBEB',
                                                    color: '#D97706',
                                                    border: '1px solid #FDE68A'
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#334155', fontStyle: 'italic', lineHeight: 1.6 }}>
                                        "{review.replayDescription}"
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ p: 2.5, textAlign: 'center', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                                        No response has been posted for this review yet.
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </PermissionWrapper>
    );
};

export default ReviewDetails;
